import './ChartBlock.scss';
import { prefixClass } from 'lib/utils';
import {
  ChartProps as ChartPropsType,
  ChartFilters,
  ChartDimensions,
  TooltipZone,
  TooltipDataProps,
} from '../../types';
import { NTToolTip } from 'presentation/components/NewTable/NTToolTip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { drawChart } from './chartBuilder';
import { useChartContext } from '../../context/ChartContext';
import React from 'react';
import ChartTooltip from './ChartTooltip';
import classNames from 'classnames';
import { useQueryState } from 'hooks/useQueryState';

type ChartBlockProps = {
  dimensions: ChartDimensions;
  chartData: ChartPropsType | null;
  chartFilters: ChartFilters;
  selectedColumn: number | string | null;
  onColumnSelectionChange?: (columnId: number | string) => void;
};

type insideResult = { zone: TooltipZone | null; foundArea: number };

const mouseInsideArea = (tooltips: TooltipZone[] | null, mouseX: number, mouseY: number): insideResult => {
  const output: insideResult = { zone: null, foundArea: -1 };
  tooltips?.forEach(zone => {
    let foundArea = -1;
    zone.areas.forEach((area, areaIndex) => {
      const { x, y, width, height } = area.rect;
      if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
        //console.log('Mouse is inside the area ', zone.title, ' ', area.title, areaIndex);
        foundArea = areaIndex;
        return;
      }
    });
    if (foundArea > -1) {
      output.zone = zone;
      output.foundArea = foundArea;
      return;
    }
  });
  return output;
};

const ChartBlock = ({
  dimensions,
  chartData,
  chartFilters,
  selectedColumn,
  onColumnSelectionChange,
}: ChartBlockProps) => {
  // Exit conditions before Hooks are called
  //if (!chartData || !chartData.columns.length) return null;
  // Chart data is passed from ChartContext
  // Local state
  const [tooltipData, setTooltipData] = useState<TooltipDataProps | null>(null);
  const [tooltipInfo, setTooltipInfo] = useState<React.ReactNode | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [tooltips, setTooltips] = useState<TooltipZone[] | null>([]);
  const { handleSelect } = useChartContext();

  const [hoveredPeriod, setHoveredPeriod] = useState<number>(-1);

  //
  const [margins, setMargins] = useState({ horizontal: 0, vertical: 0 });

  const MemoizedTooltip = useMemo(
    () => <NTToolTip position={tooltipPosition}>{tooltipInfo}</NTToolTip>,
    [tooltipInfo, tooltipPosition]
  );

  useEffect(() => {
    const computedStyle = getComputedStyle(document.documentElement);
    const horizontalMargin = parseInt(computedStyle.getPropertyValue('--ks-chart-margin-horizontal')) || 0;
    const verticalMargin = parseInt(computedStyle.getPropertyValue('--ks-chart-margin-vertical')) || 0;
    setMargins({ horizontal: horizontalMargin, vertical: verticalMargin });
  }, []);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameIdRef = useRef<number | null>(null);

  const prefix = prefixClass('chart-area');

  const scheduleDraw = useCallback(() => {
    if (frameIdRef.current !== null) return;
    frameIdRef.current = requestAnimationFrame(() => {
      frameIdRef.current = null;
      if (!chartData || !chartFilters) return;
      const tooltips = drawChart({
        canvasRef,
        dimensions,
        chartData,
        chartFilters,
        selectedPeriod: typeof selectedColumn === 'number' ? selectedColumn : 0,
        hoveredPeriod,
      });
      setTooltips(tooltips ? tooltips.filter((tooltip): tooltip is TooltipZone => tooltip !== null) : null);
    });
  }, [chartData, chartFilters, dimensions, selectedColumn, hoveredPeriod]);

  useEffect(() => {
    scheduleDraw();
  }, [scheduleDraw]);

  // Cleanup any pending animation frames on unmount
  useEffect(() => {
    return () => {
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
        frameIdRef.current = null;
      }
    };
  }, []);

  /* useEffect(() => {
    
  }, [hoveredPeriod]); */

  useEffect(() => {
    if (!tooltipData) {
      setTooltipInfo(null);
      return;
    }
    setTooltipPosition({
      x: tooltipData.rect.x + margins.horizontal + tooltipData.rect.width / 2,
      y: tooltipData.rect.y + margins.vertical,
      w: tooltipData.rect.width,
      h: tooltipData.rect.height,
    });
    setTooltipInfo(<ChartTooltip zone={tooltipData} />);
  }, [tooltipData]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      setTooltipData(null);
      const canvas = canvasRef?.current;
      if (!canvas || !chartData) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const { zone, foundArea } = mouseInsideArea(tooltips, mouseX, mouseY);
      if (!zone) return;

      //console.log('zone', zone, chartData);
      //console.log('foundArea', foundArea);
      if (zone.areas[foundArea].link) {
        const link = zone.areas[foundArea].link;
        if (link) handleSelect(link, true);
      } else if (zone.areas[foundArea].period !== undefined && zone.areas[foundArea].period !== null) {
        //console.log('period -> ', zone.areas[foundArea].period);
        const period = zone.areas[foundArea].period;
        //console.log('period', period + 1);
        if (onColumnSelectionChange && period !== undefined && period !== null) {
          //console.log('period from ChartBlock', period);
          onColumnSelectionChange(period);
        }
      }
    },
    [tooltips]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      setTooltipData(null);
      const canvas = canvasRef?.current;
      if (!canvas || !chartData) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const { zone, foundArea } = mouseInsideArea(tooltips, mouseX, mouseY);
      if (!zone) {
        setTooltipData(null);
        setHoveredPeriod(-1);
      } else {
        if (zone.areas[foundArea].period !== hoveredPeriod) {
          const period = zone.areas[foundArea].period || -1;
          setHoveredPeriod(period);
        }
        setTooltipData({ ...zone.areas[foundArea], zoneTitle: zone.title });
      }
    },
    [tooltips]
  );

  return (
    <div
      className={classNames(prefix(), {
        pointer: tooltipData && (tooltipData.link || tooltipData.periodLink),
      })}
    >
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={e => {
          setTooltipData(null);
          setHoveredPeriod(-1);
        }}
      />
      {MemoizedTooltip}
    </div>
  );
};
export default ChartBlock;
